<template>
  <v-container fluid>
    <v-dialog
      max-width="800px"
      v-model="routePlanRuleDialog">
      <v-card>
        <v-card-title>
          {{ $tc('route_plan_rule_ui.route_plan_rule', 2) }}
            <v-btn
              text
              color="primary"
              class="ml-n1"
              to="/route-plans/excluded">
              {{ $t('excluded_route_plan_ui.excluded_route_plans')}}
            </v-btn>
        </v-card-title>
        <v-card-text>
          <route-plan-rule-index ref="routePlanRuleIndex" :parent-id="customerContext.parent_id" :customer-id="customerContext.id" v-bind:show-actions="false"/>
        </v-card-text>
      </v-card>
    </v-dialog>
    <route-plan-stops-dialog ref="route-plan-stops-dialog"/>


    <div class="d-flex justify-space-between align-center">
      <h2>
        {{ $tc('route_plan', 2)}}
      </h2>
      <vf-tooltip :text="$tc('route_plan_rule_ui.route_plan_rule', 2)" right>
        <v-btn
          v-if="routePlanRuleCount > 0"
          text
          color="primary"
          class="ml-n1"
          @click="routePlanRuleDialog = true">
          {{ routePlanRuleCount }} {{ $tc('route_plan_rule_ui.route_plan_rule', routePlanRuleCountPlural) }}
        </v-btn>
      </vf-tooltip>
    </div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col md="6" cols="12">
          <v-text-field
            :label="$t('route_number')"
            v-model="searchCriteria.route_number"
            @keyup.enter="initiateSearch"
            prepend-inner-icon="mdi-road-variant">
          </v-text-field>
        </v-col>
        <v-col md="6" cols="12">
          <v-text-field
            :label="$t('stop_slash_location')"
            v-model="searchCriteria.stops"
            @keyup.enter="initiateSearch"
            prepend-inner-icon="mdi-map-marker">
          </v-text-field>
        </v-col>
        <v-col md="4" cols="12">
          <v-select
            :label="$t('status')"
            v-model="searchCriteria.status"
            :items="statusOptions"
            @keyup.enter="initiateSearch"
            prepend-inner-icon="mdi-filter"/>
        </v-col>
        <v-col class="d-flex align-center" md="4" cols="12">
          <date-picker
            v-model="searchCriteria.dispatched_after"
            :timezone="customerTimezone"
            :label="$t('start_from')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.dispatched_before"
            :timezone="customerTimezone"
            :label="$t('start_to')"
            prepend-inner-icon="mdi-calendar"
            end-of-day
            reset-on-timezone-change />
        </v-col>
        <v-col class="d-flex align-center" md="4" cols="12">
          <date-picker
            v-model="searchCriteria.created_after"
            :timezone="customerTimezone"
            :label="$t('uploaded_from')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.created_before"
            :timezone="customerTimezone"
            :label="$t('uploaded_to')"
            prepend-inner-icon="mdi-calendar"
            end-of-day
            reset-on-timezone-change />
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
        <upload-route-plan-dialog v-if="user.can('view_upload_routeplan')">
          <template #activator="{ on, attrs }">
            <v-btn class="hidden-md-and-down ml-2" outlined color="primary" v-bind="attrs" v-on="on">
              <v-icon class="mr-1 ml-n1">mdi-cloud-upload</v-icon>
              {{ $t('upload') }}
            </v-btn>
            <vf-tooltip :text="$t('upload_route_plan')">
              <v-btn class="hidden-lg-and-up"  fab text color="primary" v-bind="attrs" v-on="on">
                <v-icon>mdi-cloud-upload</v-icon>
              </v-btn>
            </vf-tooltip>
          </template>
        </upload-route-plan-dialog>
        <v-btn
          class="hidden-md-and-down ml-2"
          outlined
          color="secondary-900"
          to="/route-plans/file-upload-history">
          <v-icon class="mr-1 ml-n1">mdi-history</v-icon>
          {{ $t('file_upload_history') }}
        </v-btn>
        <vf-tooltip :text="$t('file_upload_history')">
          <v-btn
            class="hidden-lg-and-up"
            fab
            text
            color="secondary-900"
            to="/route-plans/file-upload-history">
            <v-icon>mdi-history</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <template #item.route_number="row">
          <router-link :to="`/route-plan/${row.item.id}`">{{ row.value }}</router-link>
        </template>
        <template #item.stops="row">
          <if-set :value="row.value">
            <v-btn fab x-small elevation="0" color="primary" @click="loadStopsDialog(row.item)">
              <span>{{ size(row.value) }}</span>
            </v-btn>
          </if-set>
        </template>
        <template #item.start="row">
          <multi-time-display :time="row.value" />
        </template>
        <template #item.stop="row">
          <multi-time-display :time="row.value" />
        </template>
        <template #item.duration="row">
          {{ row.item.duration | humanizeDuration({ numberOfSignificantParts: 2, separator: ', ' }) }}
        </template>
        <template #item.route_vehicle="row">
          <if-set :value="row.item.mostRecentRoute">
            <template #default="{ value }">
              <a :href="`/route/${value.id}`">
                {{ value.name }} ({{ value.vehicle.name }})
              </a>
            </template>
            <template #not-set>
              <v-btn
                class="ml-2"
                outlined
                color="primary"
                :to="`/route/create/${row.item.id}`">
                <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
                {{ $t('create_route' )}}
              </v-btn>
            </template>
          </if-set>
        </template>
        <template #item.created="row">
          <multi-time-display :time="row.value" />
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import RoutePlanStopsDialog from '@/components/route-plan/RoutePlanStopsDialog'
import UploadRoutePlanDialog from '@/components/route-plan/UploadRoutePlanDialog'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import DatePicker from '@/components/shared/DatePicker'
import VfTooltip from '@/components/shared/VfTooltip'
import IfSet from '@/components/shared/IfSet'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import QueryParamMixin from '@/components/mixins/QueryParamMixin'
import RoutePlanRuleIndex from '@/components/shared/RoutePlanRuleIndex'
import { size, some } from 'lodash'

export default {
  name: 'RoutePlanIndex',
  components: {
    RoutePlanStopsDialog,
    UploadRoutePlanDialog,
    MultiTimeDisplay,
    TablePaginationControls,
    DatePicker,
    VfTooltip,
    IfSet,
    RoutePlanRuleIndex
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    DateTimeUtilsMixin,
    BrowserUtilitiesMixin,
    ApiUtilitiesMixin,
    QueryParamMixin
  ],
  data () {
    return {
      model: 'RoutePlan',
      searchCriteria: this.getDefaultSearchCriteria(),
      routePlanRuleDialog: false,
      tableFields: [
        {
          value: 'route_number',
          text: this.$tc('route_number'),
          sortable: true
        },
        {
          value: 'stops',
          text: this.$tc('stop', 2),
          sortable: false
        },
        {
          value: 'start',
          text: this.$tc('route_start'),
          sortable: true
        },
        {
          value: 'stop',
          text: this.$tc('route_end'),
          sortable: true
        },
        {
          value: 'duration',
          text: this.$tc('duration'),
          sortable: false
        },
        {
          value: 'route_vehicle',
          text: this.$tc('route') + ' (' + this.$tc('vehicle') + ')'
        },
        {
          value: 'created',
          text: this.$tc('uploaded_at'),
          sortable: true
        }
      ],
      statusOptions: [
        {
          value: 'all',
          text: this.$t('all_route_plans')
        },
        {
          value: 'unassigned',
          text: this.$t('unlinked')
        },
        {
          value: 'assigned',
          text: this.$t('linked')
        }
      ],
      queryParams: [
        {
          name: 'route_number',
          type: String
        },
        {
          name: 'stops',
          type: String
        },
        {
          name: 'status',
          type: Array,
          validator: value => some(this.statusOptions, x => x.value === value)
        },
        {
          name: 'dispatched_after',
          type: 'Moment'
        },
        {
          name: 'dispatched_before',
          type: 'Moment'
        },
        {
          name: 'created_after',
          type: 'Moment'
        },
        {
          name: 'created_before',
          type: 'Moment'
        }
      ]
    }
  },
  computed: {
    routePlanRuleCount () {
      return parseInt(this.customerContext.getSetting('routePlanRuleCount', 0))
    },
    /**
     * If there is more than 1 route plan rule return 2, otherwise return 1.
     */
    routePlanRuleCountPlural () {
      return this.routePlanRuleCount > 1 ? 2 : 1
    }
  },
  methods: {
    size: size,
    /**
     * Returns the default search criteria.
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        route_number: null,
        stops: null,
        status: {
          value: 'all',
          text: this.$t('all_route_plans')
        },
        dispatched_after: null,
        dispatched_before: null,
        created_after: null,
        created_before: null
      }
    },

    /**
     * When customer context updates, refresh the table
     */
    customerContextChangeHandler () {
      this.initiateSearch()
      if (this.$refs.routePlanRuleIndex) {
        this.$refs.routePlanRuleIndex.refresh()
      }
    },
    /**
     * Sets our data table back to its original state before the user applied filtering and paging changes.
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.resetQueryParams()
      this.refreshResults()
    },
    /**
     * Initiate the route stops dialog
     * @param routePlan
     */
    loadStopsDialog (routePlan) {
      const title = this.$t('planned_stops_for_route_number', { route_number: routePlan.route_number })
      this.$refs['route-plan-stops-dialog'].loadRoutePlan(routePlan.id, title)
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria.
     * @return object
     */
    buildSearchCriteria () {
      const searchCriteria = {
        expand: 'routes.vehicle',
        fields: 'id,route_number,stops,start,stop,created,routes.id,routes.name,routes.vehicle.id,routes.vehicle.name',
        customer_id: this.customerContext.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.searchCriteria.route_number != null) {
        searchCriteria.route_number = this.searchCriteria.route_number
      }
      if (this.searchCriteria.stops != null) {
        searchCriteria.stops = this.searchCriteria.stops
      }
      if (this.searchCriteria.status !== 'all') {
        searchCriteria.status = this.searchCriteria.status
      }
      if (this.searchCriteria.dispatched_after != null) {
        searchCriteria.dispatched_after = this.searchCriteria.dispatched_after
      }
      if (this.searchCriteria.dispatched_before != null) {
        searchCriteria.dispatched_before = this.searchCriteria.dispatched_before
      }
      if (this.searchCriteria.created_after != null) {
        searchCriteria.created_after = this.searchCriteria.created_after
      }
      if (this.searchCriteria.created_before != null) {
        searchCriteria.created_before = this.searchCriteria.created_before
      }
      if (this.remoteData.sortBy !== null) {
        // If sorting by route_number column, override request to use natural sorting order
        const sortBy = this.remoteData.sortBy === 'route_number' ? 'route_number_numeric_natural' : this.remoteData.sortBy
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${sortBy}`
      }
      return searchCriteria
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('route_plan', 2))
    this.useQueryParamsForSearch()
    this.initiateSearch()
  }
}
</script>
